import { useLazyQuery, useMutation } from "@apollo/client";
import { Input, Label, Table } from "reactstrap";
import { EXPIRE_POSITION_ROLE, FULL_POSITION_NAME, GET_ORG_POSITIONS, GET_ORG_ROLES } from "../../../../graphql/position-gql";
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import { IOrg } from "../../../../types/org";
import { AddIcon, CancelIcon, CheckIcon, DeleteIcon } from "../../../common/icons";
import { IPosition, IPositionIdFullName } from "../../../../types/position";
import { messages } from "../../../../i18n/message";
import { LOCALES } from "../../../../i18n/locales";
import { IIndividual } from "../../../../types/individual";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { CheckGroup } from "../../../../hook/useCheckGroup";
import AddMembershipModal from "./add-memebership";
import { CategoryType } from "../../../../types/constants";
import { notifyError, notifySuccess } from "../../../../services/notification";
import confirm from "../../../common/confirm";
import useAuth from "../../../../hook/useAuth";
import { OrgReducerContext } from "../../org-context";
import Button from "../../../common/button";
import useToggle from "../../../../hook/useToggle";
import { PositionIndividualsItem } from "../common-table-components";
type RolesIndividualsProps = {
  org: IOrg;
  onRoleSelect?: (role:IPosition)=>void
  individualSelected?: (individual: IIndividual,positionRole:IPosition) => void;
  checkGroup: CheckGroup<number>;
  onMemberRemove?: (removed: boolean) => void;
  lastSelectedIndividual: (individual: IIndividual,positionRole:IPosition) => any;
};

export type RoleListRef = {
  getRoleList:() => IPosition[];
};

const RolesIndividuals = forwardRef<RoleListRef,RolesIndividualsProps>((props,ref)=>{
  const {org, onRoleSelect, individualSelected, checkGroup,onMemberRemove, lastSelectedIndividual}= props;
  const { isFrenchTranslator } = useAuth();
  const [selectedPosition,setSelectedPosition] = useState<IPosition>()
  const { isOpen: isAddMembershipModalOpen, toggle: toggleAddMembershipModalOpen } = useToggle(false);
  const [ fullPositionNames, setFullPositionNames] = useState<IPositionIdFullName[]>()
  const [state, dispatch] = useContext(OrgReducerContext);
  const [cloned,setCloned] = useState<boolean>(false);

  const [getOrgRoles, { loading, data }] = useLazyQuery(GET_ORG_ROLES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
        if (data?.org?.roles) {
            checkGroup.init(data?.org.roles.map((e: IPosition) => e.positionId))
        if (org.details?.category?.type !== CategoryType.ORGANIZATION) {
          let posdata: IPositionIdFullName[] = [];
          for (let item of data?.org?.roles) {
            if ((!item.individuals || item.individuals?.length == 0) && item.childId) {
              getFullPositonName({ variables: { positionId: item.childId }, fetchPolicy: "network-only" }).then(res => {
                if (res.data?.fullPositionName) {
                  let posItem: IPositionIdFullName = { positionId: item.childId, fullPositionName: res.data?.fullPositionName }
                  posdata.push(posItem)
                }
              }).catch(error => {
                console.error('An error occurred:', error);
                notifyError('An error occurred while getting full position name.');
              });
            }
          }
            setFullPositionNames(posdata)
   
        }
      }
    }
  });

  const [expirePositonRole,{loading:expireLoading}] = useMutation(EXPIRE_POSITION_ROLE)

  const [getFullPositonName, {loading: fpnLoading}] = useLazyQuery(FULL_POSITION_NAME)

 
  useEffect(() => {
    setCloned(!!org.details?.shareLinkId)
    getOrgRoles({ variables: { orgId: org.id } });
  }, [org]);

  const getRoleList = () =>{
    const checkedValues = checkGroup.getCheckedValues();
    if(checkedValues&&checkedValues.length>0){
      return data?.org.roles.filter((role:IPosition)=>checkedValues.includes(role.positionId))
    }
    return null;

  }

  useImperativeHandle(ref, () => ({
    getRoleList
  }));

  const expireRefetchQueries = () => {
    return [{query: GET_ORG_ROLES, variables: {  orgId: org.id||0 } },{query: GET_ORG_POSITIONS, variables: {  orgId: org.id||0 } }]
}
/*  
const onAddMembershipHandle = () => {
    //  setIsRole(false)
    toggleAddMembership();
  }
*/
  const onExpireMembershipHandle = () => {
    expireMembershipConfirm().then(() => {
      expireMembershipExecution()
    })
  }

  const expireMembershipConfirm = () => {
    return confirm({
      title: 'Remove Selected Member(s) ',
      content: messages[LOCALES.ENGLISH].INFO['5.2'],
      okText: <><DeleteIcon/> Remove</>,
      okBtnColor: 'danger',
      titleCenter: true,
      bodyCenter: true
    })
  }

  const expireMembershipExecution = () => {
    const positionIds = checkGroup.getCheckedValues()
    expirePositonRole({
      variables: { orgId: org?.id || 0, positionIds: positionIds },
      onCompleted: (data) => {
        if (data?.expirePositionOrRole && !data.expirePositionOrRole.success) {
          expireValidationConfirm(data.expirePositionOrRole.message)
        } else {
          setSelectedPosition(undefined);
          if(onMemberRemove)
          onMemberRemove(true)
          notifySuccess();
        }
      },
      awaitRefetchQueries: true,
      refetchQueries: expireRefetchQueries()
    })
  }

  const expireValidationConfirm = (message: string) => {
    return confirm({
      title: 'Warning!',
      content: message,
      okText: <><CancelIcon /> Close</>,
      okBtnColor: 'primary',
      titleCenter: true,
      bodyCenter: true,
      showCancel: false
    })
  }

  const getPositionName = (positionId: number) => {
    for (let item of fullPositionNames || []) {
      if (item.positionId == positionId)
        return item.fullPositionName
    }
    return ''
  }

  function roleSort (itemA: IPosition, itemB: IPosition): number {
    // positions are sorted with management first, but roles should never have the flag set (same db structure, so the prop is there)

    // sort by full position name
    if (itemA.fullPositionName.toUpperCase() < itemB.fullPositionName.toUpperCase()) return -1;
    if (itemA.fullPositionName.toUpperCase() > itemB.fullPositionName.toUpperCase()) return 1;

    // as of 2024-10-17, if there are multiple positions with the same name, but different individuals (like 3 roles named "Student")
    // we do NOT need to additionnaly sort by individual name
    // if this changes in the future, it can be done here

    return 0;
  }

  return (
    <div id="roles-individuals-panel">
      { (loading || expireLoading || fpnLoading) && <SpinnerOverlay /> }
      { <AddMembershipModal isOpen={isAddMembershipModalOpen} toggle={toggleAddMembershipModalOpen} org={org} /> }
      { org.details?.category?.type !== CategoryType.ORGANIZATION && !isFrenchTranslator && 
        <div className="py-3">
          <Button color="primary" style={{ marginRight: '15px' }} onClick={toggleAddMembershipModalOpen}  disabled={cloned}>
            <AddIcon /> &nbsp;<span role="region" aria-label="Add" >Add</span>
          </Button>
          <Button color="danger" style={{ marginRight: '15px' }}  disabled={checkGroup.isNoneChecked()||cloned} onClick={onExpireMembershipHandle}>
            <DeleteIcon />&nbsp;<span role="region" aria-label="Expire">Remove</span>
          </Button>
        </div>
      }
      { data?.org?.roles?.length===0 && (<div role="region" aria-label="No records found">{messages[LOCALES.ENGLISH].INFO['1.1']}</div>) }
      { data?.org?.roles?.length>0 && 
        <Table className="organizations-table fixed table-striped-rowspan" id="org_roles_individuals">
          <thead>
            <tr>
              { !isFrenchTranslator && 
                <th className="icon-column">
                  <Label for="checkAll-ri" className="hidden-text" role="region" aria-label="Select All">Select All</Label>
                  <Input id="checkAll-ri" aria-label="Select All" className="form-check-input" type='checkbox'
                    checked={checkGroup.isAllChecked()} onChange={checkGroup.onCheckAll} disabled={cloned}/>
                </th>
              }
              <th>Role Title</th>
              <th className="column-15rem">Assignment Type</th>
              <th>Individual Name</th>
            </tr>
          </thead>
            {data.org.roles.sort((a: IPosition, b: IPosition) => roleSort(a, b)).map((role: IPosition, index: number) => (
              <PositionIndividualsItem 
                  position={role} 
                  org={org} 
                  positionSelected={onRoleSelect}
                  individualSelected={individualSelected}
                  lastSelectedIndividual={lastSelectedIndividual}                   
                  state={state} 
                  isFrenchTranslator={isFrenchTranslator} 
                  checkGroup={checkGroup} 
                  cloned={cloned}
                  index={index}
                  key={index}
                  getPositionName={getPositionName} />
            ))}
        </Table>
      }
    </div>
  )
})

export default RolesIndividuals;
